import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatInTz } from '@/utils/timezone';
import { ClassBadge, JobStatusBadge } from './csrBadges';
import type { CsrCohortLeadRow } from '@/types/reports';

interface CohortLeadsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Already filtered to the leads behind the tile that was clicked. */
  rows: CsrCohortLeadRow[];
  timezone: string;
  /** Adds a Held column; only meaningful for the decided-jobs drill-down. */
  showHeld?: boolean;
}

function safeFormatDate(value: string, timezone: string) {
  try {
    return formatInTz(value, timezone, 'dd MMM yyyy HH:mm');
  } catch {
    return value;
  }
}

/** What a lead ended up as: the job if it was booked, otherwise why it was lost. */
function Outcome({ row }: { row: CsrCohortLeadRow }) {
  if (row.job_number) {
    return (
      <span className="flex items-center gap-2">
        <span className="font-mono text-xs">{row.job_number}</span>
        <JobStatusBadge status={row.job_status} />
      </span>
    );
  }
  const reason = row.lost_reason ?? (row.status === 'spam' ? 'Status: spam' : null);
  if (reason) {
    return <span className="block max-w-[280px] truncate" title={reason}>{reason}</span>;
  }
  return <span className="text-muted-foreground">-</span>;
}

/**
 * The leads behind one CSR KPI tile. Same drill-down contract as the
 * leaderboard's expanded rows: a row click opens that lead.
 */
export function CohortLeadsSheet({
  open,
  onOpenChange,
  title,
  description,
  rows,
  timezone,
  showHeld = false,
}: CohortLeadsSheetProps) {
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle className="text-xl">{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          <Badge variant="outline" className="text-sm">
            Leads: <span className="ml-1 font-semibold">{rows.length}</span>
          </Badge>
        </div>

        <div className="mt-6">
          {rows.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No leads in this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead #</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Job / reason</TableHead>
                    {showHeld && <TableHead>Held</TableHead>}
                    <TableHead>CSR</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.lead_id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => {
                        onOpenChange(false);
                        navigate(`/leads/${row.lead_id}`);
                      }}
                    >
                      <TableCell className="font-mono text-xs">
                        {row.lead_number || row.lead_id.slice(0, 8)}
                      </TableCell>
                      <TableCell><ClassBadge cls={row.class} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground"><Outcome row={row} /></TableCell>
                      {showHeld && (
                        <TableCell className="text-sm">
                          {row.held === null ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <Badge
                              variant="outline"
                              className={row.held ? 'border-success/40 text-success' : 'border-destructive/40 text-destructive'}
                            >
                              {row.held ? 'Held' : 'Not held'}
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-sm">
                        {row.csr_name || (row.csr_id ? 'Unknown' : 'Unattributed')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {safeFormatDate(row.created_at, timezone)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
